import { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { fqsRequestAll } from '@ctw/shared/api/fqs/client';
import {
  type EpisodeOfCareGraphqlResponse,
  episodeOfCareOverviewQuery,
  episodeOfCareQuery,
} from '@ctw/shared/api/fqs/queries/episode-of-care';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  QUERY_KEY_PATIENT_EPISODES_OF_CARE,
  QUERY_KEY_PATIENT_EPISODES_OF_CARE_OVERVIEW,
} from '@ctw/shared/utils/query-keys';
import type { GraphQLClient } from 'graphql-request';

interface GetAllPatientEpisodesOfCareArgs {
  telemetry: ReturnType<typeof useTelemetry>;
  graphqlClient: GraphQLClient;
  patient: PatientModel;
  /**
   * The amount of episodes of care to load at a time.
   * Maps to `first` pagination param for EpisodeOfCareConnection call.
   * Defaults to 50.
   * We must paginate by a smaller amount to avoid timeouts. See ODS-2076 for more details.
   */
  pageSize?: number;
}

/**
 * Loads every episode of care for a given patient, paginated by {@link GetAllPatientEpisodesOfCareArgs.pageSize pageSize}.
 * Promise does not resolve until all episodes of care have been loaded.
 */
export async function getAllPatientEpisodesOfCare({
  telemetry,
  graphqlClient,
  patient,
  pageSize = 50,
}: GetAllPatientEpisodesOfCareArgs) {
  const { data } = await fqsRequestAll<EpisodeOfCareGraphqlResponse>(
    telemetry,
    graphqlClient,
    episodeOfCareQuery,
    'EpisodeOfCareConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: pageSize,
      filter: {
        // we can only filter by certain things here. Other filtering occurs locally as the data is rendered.
        tag: {
          allmatch: ['https://zusapi.com/summary|TransitionsOfCare'],
        },
      },
    },
  );

  const nodes = data.EpisodeOfCareConnection.edges.map((x) => x.node);

  return nodes.map((c) => new EpisodeOfCareModel(c, []));
}

/**
 * Fetches all the episodes of care for a given patient, 50 items at a time (accounting for ODS-2076).
 */
export const usePatientEpisodesOfCare = () =>
  usePatientQuery({
    queryId: QUERY_KEY_PATIENT_EPISODES_OF_CARE,
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      return await getAllPatientEpisodesOfCare({
        telemetry,
        graphqlClient,
        patient,
      });
    },
  });

/**
 * Lightweight episodes-of-care query for the overview card. Uses a trimmed
 * GraphQL fragment that omits the recursive Encounter fetch. Safe to use where only
 * EoC-level fields are accessed (title, period, class, location labels,
 * discharge binary refs, nonelective/readmission/behavioral flags).
 *
 * Note: models returned here have an empty `aggregatedEncounters` array.
 * Do not use this hook if the downstream component needs encounter details.
 */
export const usePatientEpisodesOfCareOverview = () =>
  usePatientQuery({
    queryId: QUERY_KEY_PATIENT_EPISODES_OF_CARE_OVERVIEW,
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      const { data } = await fqsRequestAll<EpisodeOfCareGraphqlResponse>(
        telemetry,
        graphqlClient,
        episodeOfCareOverviewQuery,
        'EpisodeOfCareConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: 500,
          filter: {
            tag: {
              allmatch: ['https://zusapi.com/summary|TransitionsOfCare'],
            },
          },
        },
      );

      return data.EpisodeOfCareConnection.edges.map((x) => new EpisodeOfCareModel(x.node, []));
    },
  });

/**
 * Fetches a single episode of care by ID with the full fragment (including
 * nested encounter data). Pair with `usePatientEpisodesOfCareOverview`:
 * the overview card uses the lite query for list rendering, then the drawer
 * calls this hook to load the heavier per-record data on demand.
 */
export const useEpisodeOfCareById = (id: string | undefined) =>
  usePatientQuery({
    queryId: QUERY_KEY_PATIENT_EPISODES_OF_CARE,
    queryKey: [id],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      if (!id) {
        return undefined;
      }
      const { data } = await fqsRequestAll<EpisodeOfCareGraphqlResponse>(
        telemetry,
        graphqlClient,
        episodeOfCareQuery,
        'EpisodeOfCareConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: 1,
          filter: {
            ids: { anymatch: [id] },
          },
        },
      );

      const node = data.EpisodeOfCareConnection.edges[0]?.node;
      return node ? new EpisodeOfCareModel(node, []) : undefined;
    },
    enabled: !!id,
  });
